import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollText, Calendar, AlertCircle } from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';

interface LedgerRow {
  subscription_id: string;
  package_name: string;
  tier_label: string | null;
  audience: string;
  expires_at: string;
  credit_type: string;
  item_id: string;
  item_label: string;
  item_status: string | null;
  occurred_at: string;
  ends_at: string | null;
}

const STATUS_TONE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  approved: 'default',
  pending: 'secondary',
  draft: 'outline',
  expired: 'destructive',
  inactive: 'outline',
  rejected: 'destructive',
};

export function CreditUsageLedger() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['credit-ledger', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('subscription_credit_ledger' as any, { _user_id: user!.id });
      if (error) throw error;
      return ((data as any[]) || []) as LedgerRow[];
    },
  });

  const rows = data || [];
  const creditTypes = useMemo(() => Array.from(new Set(rows.map((r) => r.credit_type))).sort(), [rows]);
  const filtered = filter === 'all' ? rows : rows.filter((r) => r.credit_type === filter);

  const grouped = useMemo(() => {
    const map = new Map<string, LedgerRow[]>();
    filtered.forEach((r) => {
      const key = r.subscription_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-base flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-primary" /> Credit Usage Ledger
        </CardTitle>
        {creditTypes.length > 0 && (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56 h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All credit types</SelectItem>
              {creditTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">
            No credits consumed yet. Listings, projects and boosts you create while a subscription is active will appear here.
          </p>
        ) : (
          grouped.map(([subId, subRows]) => {
            const head = subRows[0];
            const expired = isPast(new Date(head.expires_at));
            return (
              <div key={subId} className="space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-sm">{head.tier_label || head.package_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{head.audience} plan · {subRows.length} credit{subRows.length === 1 ? '' : 's'} used</p>
                  </div>
                  <Badge variant={expired ? 'destructive' : 'outline'} className="gap-1">
                    {expired ? <AlertCircle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
                    {expired ? 'Expired' : 'Expires'} {formatDistanceToNow(new Date(head.expires_at), { addSuffix: true })}
                  </Badge>
                </div>
                <div className="rounded-lg border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[160px]">Credit Type</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="w-[120px]">Status</TableHead>
                        <TableHead className="w-[150px]">Used On</TableHead>
                        <TableHead className="w-[150px]">Expires</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subRows.map((r) => {
                        const itemExpired = r.ends_at ? isPast(new Date(r.ends_at)) : false;
                        return (
                          <TableRow key={`${r.credit_type}-${r.item_id}`}>
                            <TableCell className="text-xs font-medium">{r.credit_type}</TableCell>
                            <TableCell className="text-xs truncate max-w-[260px]" title={r.item_label}>{r.item_label}</TableCell>
                            <TableCell>
                              <Badge variant={STATUS_TONE[r.item_status || ''] || 'outline'} className="capitalize text-[10px]">
                                {r.item_status || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(new Date(r.occurred_at), 'd MMM yyyy')}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {r.ends_at ? (
                                <span className={itemExpired ? 'text-destructive' : ''}>
                                  {format(new Date(r.ends_at), 'd MMM yyyy')}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">With subscription</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
